import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React from 'react';
import { Badge } from '@usepepper/ui/atoms/badge';
import {
  Counter,
  CounterChangeSource,
  shadowStyle,
  Typography,
  View,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { Keyboard, LayoutChangeEvent, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MAX_CART_QUANTITY } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { OrderEntryLabel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { OrderEntryMobileCardStat } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/utils/order-entry-mobile-card-stats';
import {
  OrderGuideLabel,
  useOrderEntryLabels,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-labels';
import {
  selectSalesPortalCartItem,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { Spacing } from '@usepepper/sprout-kit';

/** Estimated card height for FlashList's recycler; keep roughly in sync with layout. */
export const ORDER_ENTRY_MOBILE_CARD_ESTIMATED_HEIGHT = 150;

// Guide-membership pills are omitted here; the card shows item statuses only.
const NO_ORDER_GUIDE_LABELS: OrderGuideLabel[] = [];

const STAT_MIN_WIDTH = 64;
const STAT_MAX_WIDTH = 160;

const STAT_FADE_WIDTH_START = 28;
const STAT_FADE_WIDTH_END = 32;
const STAT_FADE_SCROLL_RANGE = 16;

interface OrderEntryMobileItemCardProps {
  imageUrl?: string;
  isQuantityDisabled?: boolean;
  /** Row status labels (availability, essential item, lead time) shown as pills. */
  labels?: OrderEntryLabel;
  maxQuantity: number;
  minQuantity: number;
  name: string;
  onHandQuantity: number | null;
  onOpenDetail: (variantPackUUID: string) => void;
  onQuantityChange: (variantPackUUID: string, quantity: number) => void;
  /** Ordered stats rendered in the horizontal strip (Unit, On hand, SKU, …). */
  stats: OrderEntryMobileCardStat[];
  /** Pre-joined "unit · sku" meta line. */
  subtitle: string;
  variantPackUUID: string;
}

export const OrderEntryMobileItemCard: React.FC<
  OrderEntryMobileItemCardProps
> = ({
  imageUrl,
  isQuantityDisabled,
  labels,
  maxQuantity,
  minQuantity,
  name,
  onHandQuantity,
  onOpenDetail,
  onQuantityChange,
  stats,
  subtitle,
  variantPackUUID,
}) => {
  const [styles, theme] = useStyles();
  const badges = useOrderEntryLabels(labels, NO_ORDER_GUIDE_LABELS);

  // Per-item subscription so only this card re-renders on its own cart change
  const cartQuantity = useCartStore(
    (state) => selectSalesPortalCartItem(state, variantPackUUID)?.quantity ?? 0,
  );
  const isOverStock = onHandQuantity !== null && cartQuantity > onHandQuantity;
  const backgroundColor = isOverStock
    ? theme.sk.color.bg.highlight.warning
    : cartQuantity > 0
      ? theme.sk.color.bg.highlight.success
      : theme.sk.color.bg.primary.default;
  const borderColor = isOverStock
    ? theme.sk.color.icon.warning.secondary
    : cartQuantity > 0
      ? theme.sk.color.icon.positive.secondary
      : theme.sk.color.border.primary;

  const statScrollX = useSharedValue(0);
  const statLayoutWidth = useSharedValue(0);
  const statContentWidth = useSharedValue(0);

  const handleStatScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      statScrollX.value = event.contentOffset.x;
    },
  });

  const handleStatLayout = (event: LayoutChangeEvent) => {
    statLayoutWidth.value = event.nativeEvent.layout.width;
  };

  const handleStatContentSizeChange = (width: number) => {
    statContentWidth.value = width;
  };

  const statFadeStartStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      statScrollX.value,
      [0, STAT_FADE_SCROLL_RANGE],
      [0, 1],
      Extrapolation.CLAMP,
    ),
  }));

  const statFadeEndStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      statContentWidth.value - statLayoutWidth.value - statScrollX.value,
      [0, STAT_FADE_SCROLL_RANGE],
      [0, 1],
      Extrapolation.CLAMP,
    ),
  }));

  // Enforces the min/max quantity for an item
  const handleQuantityChange = (value: number, source: CounterChangeSource) => {
    if (source !== 'input') {
      Keyboard.dismiss();
    }
    if (minQuantity > 0 && value > 0 && value < minQuantity) {
      onQuantityChange(
        variantPackUUID,
        source === 'decrement' ? 0 : minQuantity,
      );
      return;
    }
    onQuantityChange(variantPackUUID, value);
  };

  return (
    <YStack
      accessibilityLabel={
        isOverStock
          ? i18n.t('sales_portal.order_entry.card.over_stock_label', { name })
          : name
      }
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderRadius="$12"
      borderWidth={1}
      gap="$12"
      paddingBottom="$8"
      paddingHorizontal="$16"
      paddingTop="$16"
    >
      <XStack
        alignItems="flex-start"
        borderBottomColor={theme.sk.color.border.primary}
        borderBottomWidth={1}
        gap="$12"
        paddingBottom="$12"
      >
        <Pressable
          accessibilityLabel={i18n.t(
            'sales_portal.order_entry.card.view_details_label',
            { name },
          )}
          accessibilityRole="button"
          onPress={() => onOpenDetail(variantPackUUID)}
          style={styles.identity}
        >
          <ItemImage
            alt={name}
            cachePolicy="memory-disk"
            contentFit="cover"
            imageURLs={imageUrl ? [imageUrl] : []}
            size={Spacing.SPACE_3XLARGE}
            style={styles.thumbnail}
          />
          <YStack flex={1} gap="$4" minWidth={0} paddingRight="$48">
            <Typography numberOfLines={2} variant="label">
              {name}
            </Typography>
            <Typography
              color={theme.sk.color.text.secondary}
              numberOfLines={1}
              size="$sm"
            >
              {subtitle}
            </Typography>
            {badges.length > 0 ? (
              <XStack flexWrap="wrap" gap="$4" marginTop="$4">
                {badges.map((badge) => (
                  <Badge
                    key={badge.key}
                    label={badge.label}
                    leftElement={badge.leftElement}
                    size={badge.size}
                    variant={badge.variant}
                  />
                ))}
              </XStack>
            ) : null}
          </YStack>
        </Pressable>
        <View
          style={[
            styles.quantityOverlay,
            cartQuantity > 0 ? styles.quantityOverlayElevated : null,
          ]}
        >
          <Counter
            disabled={isQuantityDisabled}
            editable
            key={variantPackUUID}
            max={maxQuantity > 0 ? maxQuantity : MAX_CART_QUANTITY}
            min={0}
            onChange={handleQuantityChange}
            value={cartQuantity}
          />
        </View>
      </XStack>

      <View>
        <Animated.ScrollView
          contentContainerStyle={styles.statStrip}
          horizontal
          keyboardShouldPersistTaps="handled"
          onContentSizeChange={handleStatContentSizeChange}
          onLayout={handleStatLayout}
          onScroll={handleStatScroll}
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator={false}
        >
          {stats.map((stat) => (
            <YStack
              flexShrink={0}
              gap="$4"
              key={stat.key}
              maxWidth={STAT_MAX_WIDTH}
              minWidth={STAT_MIN_WIDTH}
            >
              <Typography
                color={theme.sk.color.text.secondary}
                numberOfLines={1}
                size="$sm"
              >
                {stat.label}
              </Typography>
              <Typography
                color={
                  stat.valueColor
                    ? theme.sk.color.text[stat.valueColor]
                    : theme.sk.color.text.primary
                }
                numberOfLines={1}
                size="$sm"
                variant="label"
              >
                {stat.value}
              </Typography>
            </YStack>
          ))}
        </Animated.ScrollView>
        <Animated.View
          pointerEvents="none"
          style={[styles.statFade, styles.statFadeStart, statFadeStartStyle]}
        >
          <LinearGradient
            colors={[backgroundColor, `${backgroundColor}00`]}
            end={[1, 0]}
            start={[0, 0]}
            style={styles.statFadeGradient}
          />
        </Animated.View>
        <Animated.View
          pointerEvents="none"
          style={[styles.statFade, styles.statFadeEnd, statFadeEndStyle]}
        >
          <LinearGradient
            colors={[`${backgroundColor}00`, backgroundColor]}
            end={[1, 0]}
            start={[0, 0]}
            style={styles.statFadeGradient}
          />
        </Animated.View>
      </View>
    </YStack>
  );
};

const useStyles = createStyles((theme) => ({
  identity: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
  },
  // Floats over the header's top-right; the name column reserves its width.
  quantityOverlay: {
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 2,
  },
  quantityOverlayElevated: {
    ...shadowStyle('soft1', 4),
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusRound,
  },
  statFade: {
    bottom: 0,
    position: 'absolute',
    top: 0,
  },
  statFadeEnd: {
    right: 0,
    width: STAT_FADE_WIDTH_END,
  },
  statFadeGradient: {
    flex: 1,
  },
  statFadeStart: {
    left: 0,
    width: STAT_FADE_WIDTH_START,
  },
  statStrip: {
    gap: theme.sk.spacing.spaceMedium,
    paddingRight: theme.sk.spacing.spaceLarge,
  },
  thumbnail: {
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
}));
